import React from 'react'
import { Box, Text } from '@chakra-ui/react'
import { Textarea } from '@chakra-ui/react'

import SharedSelectableOption from './wrappers/shared-selectable-option'
import SharedStack from './wrappers/shared-stack'
import { useDisclosure } from '@chakra-ui/react'
import CopyTemplateModal from '~components/chats/chat/input/templates/modal'
import { PatientIdAndName } from '~components/chats/chat/input/templates/types'
import { ClientPreviewFieldsFragment } from '~graphql/generated/graphql'
import MessageTemplatesProvider from '~components/engagement/message-templates/provider'
import { useClinic } from '~contexts/clinic-context'
import SharedInfoText from './texts/shared-info-text'
import SharedHelpText from './form/shared-help-text'

interface Props {
    client: ClientPreviewFieldsFragment
    patient: PatientIdAndName
    explanation: string
    setExplanation: (explanation: string) => void
    isChangeRequest?: boolean
    isForReschedule?: boolean
    isDenial: boolean
    setIsDenial: (isDenial: boolean) => void
}

const DenialReasonForm: React.FC<Props> = ({
    client,
    patient,
    explanation,
    setExplanation,
    isChangeRequest,
    isForReschedule,
    isDenial,
    setIsDenial,
}) => {
    const { clinic } = useClinic()
    const { isOpen, onOpen, onClose } = useDisclosure()

    if (!clinic || !client || !patient) {
        return (
            <>
                There is an issue with the data on this appointment. Please
                contact support.
            </>
        )
    }

    return (
        <MessageTemplatesProvider clinic={clinic} isOpen={isOpen}>
            {!isForReschedule && (
                <>
                    <Text fontWeight="semibold">
                        Please select your desired action.
                    </Text>
                    <SharedHelpText>
                        Clients will be notified through SMS, email, and the app
                        (if applicable).
                    </SharedHelpText>
                    <SharedStack row mt={2} mb={8} alignItems="stretch">
                        <SharedSelectableOption
                            isSelected={!isDenial}
                            onClick={() => setIsDenial(false)}
                            label="Request a new time is selected."
                            helpText="This will cancel the current request and send a link to select a new time with the same details along with the notes below."
                            flex={1}
                        />
                        <SharedSelectableOption
                            isSelected={isDenial}
                            onClick={() => setIsDenial(true)}
                            label="Deny this request."
                            helpText="This will cancel the current request and send a message with the notes below."
                            flex={1}
                        />
                    </SharedStack>
                </>
            )}
            <Box>
                <Text fontWeight="semibold" mb={2}>
                    Provide some more information for why you are{' '}
                    {isChangeRequest ? 'requesting' : 'denying'} this
                    {isForReschedule ? ' reschedule' : ' appointment'}
                    {isChangeRequest ? ' be changed.' : '.'}
                </Text>
                <SharedStack row alignItems="stretch">
                    <SharedSelectableOption
                        isSelected={false}
                        onClick={() =>
                            setExplanation('Doctor is not available')
                        }
                        label="Doctor is not available"
                        helpText="Quick Action"
                        flex={1}
                    />
                    <SharedSelectableOption
                        isSelected={false}
                        onClick={() => setExplanation('Clinic is closed')}
                        label="Clinic is closed"
                        helpText="Quick Action"
                        flex={1}
                    />
                    <SharedSelectableOption
                        isSelected={false}
                        onClick={() => onOpen()}
                        label="Message Template"
                        helpText="Select from a pre-written message template."
                        flex={1}
                    />
                </SharedStack>
                <Textarea
                    rounded="xl"
                    mt={6}
                    value={explanation}
                    onChange={e => setExplanation(e.target.value)}
                    focusBorderColor="brand.500"
                    placeholder="Notes..."
                    minH="100px"
                />
                <CopyTemplateModal
                    isOpen={isOpen}
                    onClose={onClose}
                    clinic={clinic}
                    client={client}
                    patient={patient}
                    setText={setExplanation}
                />
            </Box>
        </MessageTemplatesProvider>
    )
}

export default DenialReasonForm
